import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, DollarSign, Users, MousePointerClick, Search, Mail,
  TrendingUp, RefreshCw, LogOut, Calendar, Eye,
  FileText, Globe, ShoppingCart, Sparkles, Activity, Link2, FlaskConical, Plus, Trash2, Play, Pause, Trophy, Star, CheckCircle2, XCircle, MessageSquare, Film, Video, Send, Phone, Zap, MapPin, Dumbbell, Package, Target, ChevronRight, Pin, PinOff, Home, Instagram, Facebook, Wand2, Copy, Filter, Megaphone, Handshake
} from 'lucide-react';
import AutoBlogTab from './admin/AutoBlogTab';
import CampaignsTab from './admin/CampaignsTab';
import InventoryTab from './admin/InventoryTab';
import OverviewTab from './admin/OverviewTab';
import WeeklyDigestCard from './admin/WeeklyDigestCard';
import SeoTab from './admin/SeoTab';
import RevenueTab from './admin/RevenueTab';
import TrafficTab from './admin/TrafficTab';
import AffiliatesTab from './admin/AffiliatesTab';
import LeadsTab from './admin/LeadsTab';
import ConversionsTab from './admin/ConversionsTab';
import OrdersTab from './admin/OrdersTab';
import ABTestsTab, { StackABTestsSection } from './admin/ABTestsTab';
import GymContentTab from './admin/GymContentTab';
import ContentReviewsTab from './admin/ContentReviewsTab';
import ProductReviewsTab from './admin/ProductReviewsTab';
import RacePlansTab from './admin/RacePlansTab';
import SMSTab from './admin/SMSTab';
import FeaturedArticleTab from './admin/FeaturedArticleTab';
import MetaVaultTab from './admin/MetaVaultTab';
import GymSubmissionsTab from './admin/GymSubmissionsTab';
import GymReferralsTab from './admin/GymReferralsTab';
import LocatorTab from './admin/LocatorTab';
import PreOrdersTab from './admin/PreOrdersTab';
import CTAAnalyticsTab from './admin/CTAAnalyticsTab';
import ABTestingPanel from './admin/ABTestingPanel';
import SeededArticlesTab from './admin/SeededArticlesTab';
import PartnersTab from './admin/PartnersTab';
import GscDiffTab from './admin/GscDiffTab';
import DirectoryTab from './admin/DirectoryTab';
import FunnelTab from './admin/FunnelTab';
import RunClubOutreachTab from './admin/RunClubOutreachTab';
import PeptideFunnelTab from './admin/PeptideFunnelTab';
import GearBoxTab from './admin/GearBoxTab';
import { KpiCard, BarViz, DailyActivityChart, LoadingSkeleton } from './admin/_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'seo', label: 'SEO & Search', icon: Search },
  { id: 'gscdiff', label: 'GSC Diff', icon: Globe },
  { id: 'revenue', label: 'Revenue & Sales', icon: DollarSign },
  { id: 'traffic', label: 'Traffic & Clicks', icon: MousePointerClick },
  { id: 'conversions', label: 'Conversions', icon: TrendingUp },
  { id: 'abtests', label: 'A/B Tests', icon: FlaskConical },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'raceplans', label: 'Race Plans', icon: Trophy },
  { id: 'reviews', label: 'Reviews', icon: MessageSquare },
  { id: 'productreviews', label: 'Product Reviews', icon: Star },
  { id: 'gymcontent', label: 'Gym Content', icon: Film },
  { id: 'affiliates', label: 'Affiliates', icon: Users },
  { id: 'leads', label: 'Leads & Forms', icon: Mail },
  { id: 'funnel', label: 'Funnel', icon: Filter },
  { id: 'peptidefunnel', label: 'Peptide Funnel', icon: Activity },
  { id: 'runcluboutreach', label: 'Run Club Outreach', icon: Megaphone },
  { id: 'sms', label: 'SMS', icon: Phone },
  { id: 'campaigns', label: 'Campaigns', icon: Send },
  { id: 'inventory', label: 'Inventory', icon: ShoppingCart },
  { id: 'featured', label: 'Featured Article', icon: Pin },
  { id: 'gyms', label: 'Gym Directory', icon: Dumbbell },
  { id: 'dfwdirectory', label: 'Gym Pipeline', icon: MapPin },
  { id: 'gymreferrals', label: 'Gym Referrals', icon: MousePointerClick },
  { id: 'locator', label: 'Recovery Locator', icon: MapPin },
  { id: 'preorders', label: 'Pre-Orders', icon: Package },
  { id: 'gearbox', label: 'Gear Boxes', icon: Package },
  { id: 'ctaanalytics', label: 'CTA Analytics', icon: Target },
  { id: 'metavault', label: 'Content Vault', icon: Sparkles },
  { id: 'autoblog', label: 'Auto-Blog', icon: Wand2 },
  { id: 'partners', label: 'Partners', icon: Handshake },
  { id: 'seeded', label: 'Seeded Articles', icon: FileText },
];

const PERIODS = [
  { label: 'All Time', value: 'all' },
  { label: '7 Days', value: '7' },
  { label: '30 Days', value: '30' },
  { label: '90 Days', value: '90' },
];

const EXTERNAL_TOOLS = [
  { name: 'Google Analytics', icon: BarChart3, color: '#F59E0B', url: 'https://analytics.google.com' },
  { name: 'Search Console', icon: Search, color: '#10B981', url: 'https://search.google.com/search-console' },
  { name: 'HubSpot CRM', icon: Mail, color: '#F97316', url: 'https://app.hubspot.com' },
  { name: 'Stripe Dashboard', icon: DollarSign, color: '#8B5CF6', url: 'https://dashboard.stripe.com' },
  { name: 'Resend', icon: Mail, color: '#3B82F6', url: 'https://resend.com/emails' },
];

// Login Screen
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!resp.ok) throw new Error('Invalid password');
      const data = await resp.json();
      onLogin(data.token);
    } catch {
      setError('Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center" data-testid="admin-login">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">NinjAthlete Analytics</p>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent text-gray-900"
            placeholder="Enter admin password"
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// KPI Card

// Bar visualization

// Mini sparkline chart using CSS

// Overview Tab


// SEO Tab

// Revenue Tab

// Traffic Tab

// Affiliates Tab

// Leads Tab

// Conversions Tab - Article to Affiliate Click Attribution


// Orders Management Tab


// A/B Tests Tab


// Gym Content Management Tab


// Content Reviews Admin Tab

// Product Reviews Admin Tab

// Race Plans Tab

// SMS Tab





// Campaigns Tab


// Featured Articles — pin/unpin up to 3 articles to the homepage hero + sub-features grid



// Inventory Management Tab


// ─── Meta Content Vault Tab (IG + FB ingestion + AI auto-drafts) ───



// ─── Auto-Blog Tab (daily Claude-drafted EEAT articles, draft → approve flow) ───



// Main Dashboard
export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('all');
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(false);

  const handleLogin = (t) => {
    setToken(t);
    sessionStorage.setItem('admin_token', t);
  };

  const handleLogout = () => {
    setToken(null);
    sessionStorage.removeItem('admin_token');
  };

  const fetchTabData = useCallback(async (tab, p) => {
    if (!token) return;
    setLoading(true);
    const endpoints = {
      overview: '/api/admin/overview',
      seo: '/api/admin/seo',
      revenue: '/api/admin/revenue',
      traffic: '/api/admin/traffic',
      conversions: '/api/admin/article-conversions',
      abtests: null, // Self-managed tab
      orders: null, // Self-managed tab
      raceplans: '/api/admin/race-plan-stats',
      reviews: null, // Self-managed tab
      gymcontent: null, // Self-managed tab
      affiliates: '/api/admin/affiliates',
      leads: '/api/admin/leads',
      funnel: '/api/admin/funnel',
      ctaanalytics: '/api/admin/cta-analytics',
    };
    const endpoint = endpoints[tab];
    if (!endpoint) { setLoading(false); return; }
    const periodParam = p && p !== 'all' ? `?period=${p}` : '';
    try {
      const resp = await fetch(`${API_URL}${endpoint}${periodParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 401) { handleLogout(); return; }
      const data = await resp.json();
      // For affiliates tab, also fetch product-level analytics and peptide stack analytics
      if (tab === 'affiliates') {
        try {
          const [prodResp, stackResp] = await Promise.all([
            fetch(`${API_URL}/api/admin/affiliate-products${periodParam}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/api/admin/peptide-stack-analytics${periodParam}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          if (prodResp.ok) data.product_analytics = await prodResp.json();
          if (stackResp.ok) data.stack_analytics = await stackResp.json();
        } catch (e) { console.error('Failed to fetch analytics:', e); }
      }
      setTabData(prev => ({ ...prev, [`${tab}_${p || 'all'}`]: data }));
    } catch (err) {
      console.error(`Failed to fetch ${tab} data:`, err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchTabData(activeTab, period);
  }, [activeTab, period, token, fetchTabData]);

  const currentData = tabData[`${activeTab}_${period}`];

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900">Business Dashboard</h1>
                <p className="text-[10px] text-gray-400">NinjAthlete Analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchTabData(activeTab, period)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-red-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Period Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500 mr-1">Period:</span>
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                period === p.value
                  ? 'bg-teal-500 text-white'
                  : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {activeTab === 'overview' && <OverviewTab data={currentData} />}
        {activeTab === 'seo' && <SeoTab data={currentData} />}
        {activeTab === 'gscdiff' && <GscDiffTab />}
        {activeTab === 'revenue' && <RevenueTab data={currentData} />}
        {activeTab === 'traffic' && <TrafficTab data={currentData} />}
        {activeTab === 'conversions' && <ConversionsTab data={currentData} />}
        {activeTab === 'abtests' && (
          <div className="space-y-8">
            <ABTestingPanel />
            <StackABTestsSection />
          </div>
        )}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'raceplans' && <RacePlansTab data={currentData} />}
        {activeTab === 'reviews' && <ContentReviewsTab />}
        {activeTab === 'productreviews' && <ProductReviewsTab />}
        {activeTab === 'gymcontent' && <GymContentTab />}
        {activeTab === 'affiliates' && <AffiliatesTab data={currentData} />}
        {activeTab === 'leads' && <LeadsTab data={currentData} />}
        {activeTab === 'funnel' && <FunnelTab data={currentData} />}
        {activeTab === 'peptidefunnel' && <PeptideFunnelTab />}
        {activeTab === 'runcluboutreach' && <RunClubOutreachTab />}
        {activeTab === 'sms' && <SMSTab />}
        {activeTab === 'campaigns' && <CampaignsTab />}
        {activeTab === 'inventory' && <InventoryTab />}
        {activeTab === 'featured' && <FeaturedArticleTab />}
        {activeTab === 'gyms' && <GymSubmissionsTab />}
        {activeTab === 'dfwdirectory' && <DirectoryTab />}
        {activeTab === 'gymreferrals' && <GymReferralsTab />}
        {activeTab === 'locator' && <LocatorTab />}
        {activeTab === 'preorders' && <PreOrdersTab />}
        {activeTab === 'gearbox' && <GearBoxTab />}
        {activeTab === 'ctaanalytics' && <CTAAnalyticsTab data={currentData} />}
        {activeTab === 'metavault' && <MetaVaultTab />}
        {activeTab === 'autoblog' && <AutoBlogTab />}
        {activeTab === 'partners' && <PartnersTab />}
        {activeTab === 'seeded' && <SeededArticlesTab />}
      </div>
    </div>
  );
}
